import { DecryptedPatient } from '@icure/cardinal-sdk'
import React, { ReactElement, useEffect } from 'react'
import { CustomModal } from '../../../common/CustomModal'
import './index.less'
import '@icure/be-hubs-access/styles.css'

import { CertificateBadge, DocumentTree, HealthcarePartyBadge, PatientConsentBadge, TherapeuticLinkBadge, useBeCardinalContext } from '@icure/be-hubs-access'
import { useAppSelector } from '../../../../core/hooks'
import { HubStatus } from '@icure/be-hubs-access/provider/UseBeCardinalContext'
import { getPatientDataFormated } from '../../../../helpers/patientDataManipulations'
import { usePDF } from '@react-pdf/renderer'
import { useFindFormsByHcPartyPatientQuery } from '../../../../core/api/formApi'
import { useGeneratePdfReportQuery } from '../../../../core/api/pdfReportApi'
import { translateSection } from '../../../../helpers/translate'
import { MedicalReportPDF } from '../../components/MedicalReportPDF'
import { Button, Collapse, Form, Input } from 'antd'
import { createSelector } from '@reduxjs/toolkit'
import { CardinalApiState } from '../../../../core/services/auth.api'

interface ModalUploadReportProps {
  isVisible: boolean
  onClose: () => void
  patient: DecryptedPatient
}

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    ssin: cardinalApi.user?.identifier?.find((id) => id.type?.type === 'SSIN')?.value ?? cardinalApi.healthcareParty?.ssin,
    healthcarePartyId: cardinalApi.healthcareParty?.id,
    parentHealthcarePartyId: cardinalApi.healthcareParty?.parentId,
  }),
)

export const ModalUploadReport = ({ isVisible, onClose, patient }: ModalUploadReportProps): ReactElement => {
  const handleOnClose = () => {
    onClose()
  }

  const handleOnUpload = () => {
    setShouldUploadPdf(true)
  }

  const patientSsin = patient.ssin ?? patient.identifier?.find((id) => id.type?.type === 'SSIN')?.value
  const { ssin, healthcarePartyId, parentHealthcarePartyId } = useAppSelector(reduxSelector)
  const [hubStatus, setHubStatus] = React.useState<HubStatus | undefined>(undefined)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [shouldUploadPdf, setShouldUploadPdf] = React.useState<boolean>(false)
  const [createPatientDialogOpen, setCreatePatientDialogOpen] = React.useState<boolean>(false)
  const { isCertificateValid, loadPatientFromHub, createPatientInHub, uploadDocumentToHub } = useBeCardinalContext()

  const refresh = async () => {
    if (!patientSsin) return
    setIsLoading(true)
    try {
      const h = await loadPatientFromHub(patientSsin.replace(/[^0-9]/g, ''), patient.firstName, patient.lastName)
      setHubStatus(h)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refresh().catch((e) => {
      console.error('Error loading patient hub status:', e)
    })
  }, [patientSsin, isCertificateValid])

  const [uploading, setUploading] = React.useState<boolean>(false)
  const [uploadError, setUploadError] = React.useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = React.useState<string | null>(null)

  const { userNameOneString } = getPatientDataFormated(patient)
  const [pdfInstance, updatePdfInstance] = usePDF()

  const { data: listOfForms } = useFindFormsByHcPartyPatientQuery(
    {
      hcPartyId: parentHealthcarePartyId ?? healthcarePartyId ?? '',
      patient,
    },
    { skip: (!healthcarePartyId && !parentHealthcarePartyId) || !patient },
  )
  const { data: pdfData } = useGeneratePdfReportQuery({ language: patient?.languages?.[0], patient, formId: listOfForms?.[0]?.id }, { skip: !patient || !listOfForms })
  const patientData = pdfData?.patientData
  const medicalOverview = pdfData?.medicalOverview
  const initialEvaluationData = pdfData?.initialEvaluationData
  const wellBeingSummary = pdfData?.wellBeingSummary

  const [patientForm] = Form.useForm()

  const handleCreatePatient = (values: any) => {
    if (!patientSsin) return
    // Implement patient creation logic here
    console.log('Creating patient with values:', values)
    createPatientInHub({
      ssin: patientSsin.replace(/[^0-9]/g, ''),
      idCardNumber: values.idCardNumber,
      firstName: values.firstName,
      lastName: values.lastName,
    })
      .then(() => {
        setCreatePatientDialogOpen(false)
        refresh().catch((e) => {
          console.error('Error loading patient hub status after creation:', e)
        })
      })
      .catch((err: unknown) => {
        console.error('Error creating patient in hub:', err)
      })
  }

  useEffect(() => {
    if (isVisible && shouldUploadPdf && patientData && medicalOverview && initialEvaluationData && wellBeingSummary) {
      translateSection(patient?.languages?.[0] ?? 'fr', 'medicalReportPDF').then((translations) => {
        const medicalReportPDF = (
          <MedicalReportPDF
            translations={translations}
            patientData={patientData}
            medicalOverview={medicalOverview}
            initialEvaluationData={initialEvaluationData}
            wellBeingSummary={wellBeingSummary}
          />
        )
        updatePdfInstance(medicalReportPDF)
      })
    }
  }, [isVisible, patientData, medicalOverview, initialEvaluationData, wellBeingSummary, shouldUploadPdf])

  useEffect(() => {
    if (!pdfInstance.blob || !patientSsin || !shouldUploadPdf || uploading) return
    setUploading(true)
    setShouldUploadPdf(false)
    setUploadError(null)
    setUploadSuccess(null)
    try {
      uploadDocumentToHub(
        { ...patient, ssin: patientSsin.replace(/[^0-9]/g, '') },
        new File([pdfInstance.blob], `Medical_Report_${(userNameOneString ?? patientSsin).replace(' ', '_')}.pdf`, { type: 'application/pdf' }),
        'diarynote',
      )
        .then(() => {
          setUploadSuccess(`Uploaded PDF report to hubs for patient SSIN ${patientSsin}.`)
        })
        .catch((err: unknown) => {
          console.error('Error uploading document to hub:', err)
          const message = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Upload failed'
          setUploadError(message)
        })
        .finally(() => {
          setUploading(false)
          setShouldUploadPdf(false)
        })
    } catch (err: unknown) {
      console.error('Error uploading document to hub:', err)
      const message = err instanceof Error ? err.message : typeof err === 'string' ? err : 'Upload failed'
      setUploadError(message)
    }
  }, [pdfInstance.blob, shouldUploadPdf, patientSsin])

  return (
    <CustomModal
      isVisible={isVisible}
      handleClose={handleOnClose}
      secondaryBtnTitle="Cancel"
      handleClickPrimaryBtn={handleOnUpload}
      primaryBtnTitle="Upload Report"
      primaryBtnDisabled={uploading || !isCertificateValid}
      title="Upload Report to Hubs"
    >
      <div className="modalUploadReport">
        {!patient.ssin ? (
          <div>Patient SSIN not available.</div>
        ) : !ssin ? (
          <div>The SSIN of the logged-in practitioner is not available.</div>
        ) : (
          <>
            <div className="hubHeader">
              <div className="labelsColumn">
                <div className="label">Your hub status (SSIN: {ssin}) :</div>
                <div className="label">Patient status (SSIN: {patientSsin}) :</div>
              </div>
              <div className="badgesColumn">
                <>
                  {patientSsin ? (
                    <div className="badgeColumn left">
                      <CertificateBadge />
                      {hubStatus ? (
                        <PatientConsentBadge
                          patientSsin={patientSsin.replace(/[^0-9]/g, '')}
                          hubConsent={hubStatus.hubPatientConsent}
                          fedConsent={hubStatus.fedPatientConsent}
                          patientLastName={hubStatus.patientDetails?.lastName ?? patient.lastName}
                          patientFirstName={hubStatus.patientDetails?.firstName ?? patient.firstName}
                          onConsentUpdated={() => refresh()}
                        />
                      ) : isLoading ? (
                        'Loading patient hub status...'
                      ) : (
                        'No patient hub status available.'
                      )}
                    </div>
                  ) : (
                    'No patient SSIN provided.'
                  )}

                  <div className="badgeColumn right">
                    <HealthcarePartyBadge />
                    {hubStatus && patientSsin ? (
                      <TherapeuticLinkBadge
                        patientSsin={patientSsin.replace(/[^0-9]/g, '')}
                        hubTherapeuticLinks={hubStatus.hubPatientTherapeuticLinks}
                        fedTherapeuticLinks={hubStatus.fedPatientTherapeuticLinks}
                        onLinkUpdated={() => refresh()}
                      />
                    ) : null}
                  </div>
                </>
              </div>
            </div>
            {hubStatus && !isLoading && !hubStatus?.patientDetails && (
              <>
                <div className="w-full">
                  <div className="pt-6">
                    <div className="flex gap-2">
                      <div className="h-8 pt-1 pb-1 accent-red-600">Patient with SSIN {patientSsin} not found in the system.</div>
                      <Button type="primary" className="flex items-center gap-2" onClick={() => setCreatePatientDialogOpen(true)}>
                        Create new patient
                      </Button>
                    </div>
                  </div>
                </div>
                <CustomModal
                  isVisible={createPatientDialogOpen}
                  handleClose={() => setCreatePatientDialogOpen(false)}
                  title={`Create Patient ${patientSsin} on the Hubs`}
                  primaryBtnTitle="Create"
                  secondaryBtnTitle="Cancel"
                  handleClickPrimaryBtn={() => {
                    patientForm.submit()
                  }}
                >
                  <Form className="modalCreatePatient__form" form={patientForm} layout="vertical" onFinish={(values) => handleCreatePatient(values)}>
                    <Form.Item name="idCardNumber" label="ID Card Number" required={true}>
                      <Input id="idCardNumber" placeholder="Enter ID card number" />
                    </Form.Item>
                    <Form.Item name="firstName" label="First Name" required={true}>
                      <Input id="firstName" placeholder="Enter first name" />
                    </Form.Item>
                    <Form.Item name="lastName" label="Last Name" required={true}>
                      <Input id="lastName" placeholder="Enter last name" />
                    </Form.Item>
                  </Form>
                </CustomModal>
              </>
            )}
            {hubStatus?.patientDetails && (
              <div className="hubDetails">
                <div className="uploadControls" style={{ marginTop: 12 }}>
                  {(uploadSuccess || uploadError) && (
                    <div className="uploadStatus" style={{ marginTop: 8 }}>
                      {uploadSuccess && <div className="success">{uploadSuccess}</div>}
                      {uploadError && <div className="error">{uploadError}</div>}
                    </div>
                  )}
                </div>
                <Collapse
                  items={[
                    {
                      key: '1',
                      label: 'Hubs document',
                      children: (
                        <div className="documentsSection">{patientSsin ? <DocumentTree patientSsin={patientSsin.replace(/[^0-9]/g, '')} /> : 'No patient SSIN provided.'}</div>
                      ),
                    },
                  ]}
                />
              </div>
            )}
          </>
        )}
      </div>
    </CustomModal>
  )
}
